import React from "react";
import { ShieldCheck, ShieldOff, Eye, Info } from "lucide-react";

const STATE = {
  enabled: { label: "Enforced", cls: "ok", icon: ShieldCheck },
  enabledForReportingButNotEnforced: { label: "Report-only", cls: "warn", icon: Eye },
  disabled: { label: "Disabled", cls: "", icon: ShieldOff },
};

const CONTROL = { mfa: "Require MFA", block: "Block", compliantDevice: "Compliant device", domainJoinedDevice: "Hybrid-joined device", approvedApplication: "Approved app", compliantApplication: "App protection policy", passwordChange: "Password change" };

const scope = (p) => {
  const users = p.include_users || [];
  if (users.includes("All")) return "All users";
  if (users.includes("GuestsOrExternalUsers")) return "Guests / external";
  const groups = p.group_names || p.include_groups || [];
  const n = users.length + groups.length;
  if (!n) return "No users targeted";
  return [users.length ? `${users.length} users` : "", groups.length ? `groups: ${groups.slice(0, 3).join(", ")}${groups.length > 3 ? "…" : ""}` : ""].filter(Boolean).join(" · ");
};

export default function CaPolicies({ ca, error, onShowGaps, source }) {
  if (!ca) {
    return (
      <section className="ws-card glass" data-testid="ca-unavailable">
        <h2>Conditional Access</h2>
        <div className="ws-alert">
          <Info size={14} />{" "}
          {source === "csv" ? (
            <span>This tenant was imported from a CSV export - Conditional Access policies are only available through a Microsoft Graph connection.</span>
          ) : (
            <span>
              Conditional Access policies were not read. Grant <b>Policy.Read.All</b> to the app registration and re-run the scan. {error && <span className="mono small">({error})</span>}
            </span>
          )}
        </div>
      </section>
    );
  }
  return (
    <section className="ws-card glass" data-testid="ca-policies">
      <div className="ws-card-head">
        <h2>Conditional Access policies</h2>
        <div className="ws-chips">
          <span className="mini-chip ok">{ca.enabled} enforced</span>
          <span className="mini-chip warn">{ca.report_only} report-only</span>
          <span className="mini-chip">{ca.disabled} disabled</span>
        </div>
      </div>
      <p className="muted">
        Policies are read as tenant configuration (not evaluated per sign-in).{" "}
        {onShowGaps && (
          <button type="button" className="link-btn" onClick={onShowGaps} data-testid="ca-show-gaps">
            See which targeted users lack the Entra ID / Intune license the policy needs →
          </button>
        )}
      </p>
      <div className="ws-table-wrap">
        <table className="ws-table">
          <thead>
            <tr>
              <th>Policy</th>
              <th>State</th>
              <th>Applies to</th>
              <th>Apps</th>
              <th>Controls</th>
              <th>Risk conditions</th>
              <th>Modified</th>
            </tr>
          </thead>
          <tbody>
            {ca.policies.map((p) => {
              const st = STATE[p.state] || { label: p.state, cls: "", icon: Info };
              const risk = [...(p.sign_in_risk || []).map((r) => `sign-in ${r}`), ...(p.user_risk || []).map((r) => `user ${r}`)];
              return (
                <tr key={p.id} data-testid="ca-row">
                  <td className="sku-name">{p.name}</td>
                  <td>
                    <span className={`status-pill ${st.cls}`}>
                      <st.icon size={11} /> {st.label}
                    </span>
                  </td>
                  <td>{scope(p)}</td>
                  <td>{(p.include_apps || []).includes("All") ? "All cloud apps" : `${(p.include_apps || []).length} apps`}</td>
                  <td>
                    <div className="lic-chips">
                      {(p.grant_controls || []).map((c) => (
                        <span key={c} className="mini-chip">
                          {CONTROL[c] || c}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>{risk.length ? <span className="mini-chip warn">{risk.join(", ")} · needs Entra P2</span> : "—"}</td>
                  <td>{p.modified ? new Date(p.modified).toLocaleDateString() : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
